import {Paper, Table, TableBody, TableContainer} from '@mui/material';
import React, {useMemo} from 'react';
import PlayerSessionListItem from './PlayerSessionListItem';
import {compare} from '../../helpers';
import {positions} from '../../services/teamService';
import {getLatestBaselineByType} from '../../services/baselineService';
import {getMean, getSum} from '../../services/mathService';
import {Baseline, PlayerSession} from '../../interfaces';
import {useTranslation} from 'react-i18next';

interface Props {
  navigateUrlPrefix: string;
  // Grouped sessions navigate here instead when set (e.g. the admin view,
  // where the group route needs the profile id in the path).
  groupNavigateUrlPrefix?: string;
  playerSessions: PlayerSession[];
  baselines?: Baseline[];
  showProfile?: boolean;
  showDate?: boolean;
  sortBy?: 'default' | 'teamPositionThenNameAsc';
}

const PlayerSessionsList: React.FC<Props> = ({
  navigateUrlPrefix,
  groupNavigateUrlPrefix,
  playerSessions,
  baselines,
  showProfile,
  showDate,
  sortBy,
}) => {
  const {t} = useTranslation();

  const groupedPlayerSessions: PlayerSession[] = useMemo(() => {
    const groupedPS: PlayerSession[] = [];
    if (playerSessions.length) {
      playerSessions.slice().map(ps => {
        if (!ps.teamSession?.teamSessionGroupId) {
          return groupedPS.push(ps);
        }
        const teamSessionGroupId = ps.teamSession.teamSessionGroupId;
        const allGroupedSessions = playerSessions.filter(
          groupPS =>
            groupPS.teamSession?.teamSessionGroupId === teamSessionGroupId,
        );
        if (
          !teamSessionGroupId ||
          allGroupedSessions.length < 1 ||
          groupedPS.some(
            gps => gps.teamSession?.teamSessionGroupId === teamSessionGroupId,
          )
        ) {
          return null;
        }
        // sum new session values
        const totalHustlePoints = allGroupedSessions.reduce((curr, acc) => {
          return curr + (acc.hustleScore || 0);
        }, 0);
        const sortedSessions = [...allGroupedSessions].sort(
          (a, b) =>
            new Date(a.startTime || '').valueOf() -
            new Date(b.startTime || '').valueOf(),
        );
        const firstSession = sortedSessions[0];
        const lastSession = sortedSessions[sortedSessions.length - 1];
        // Load is summed across the group; undefined (not 0) when no session
        // in the group reported one.
        const groupLoadData = allGroupedSessions
          .map(ps => ps.cumulative3DPlayerLoadV2)
          .filter((v): v is number => v != null && v >= 0);
        const newSessionValues = {
          hasVideo: allGroupedSessions.some(ps => ps.hasVideo),
          hasPlayerData: allGroupedSessions.every(ps => ps.hasPlayerData),
          activeTime: allGroupedSessions.reduce((curr, acc) => {
            return curr + (acc.activeTime || 0);
          }, 0),
          timeOnIce: allGroupedSessions.reduce((curr, acc) => {
            return curr + (acc.timeOnIce || 0);
          }, 0),
          strideCountLeft: allGroupedSessions.reduce((curr, acc) => {
            return curr + (acc.strideCountLeft || 0);
          }, 0),
          strideCountRight: allGroupedSessions.reduce((curr, acc) => {
            return curr + (acc.strideCountRight || 0);
          }, 0),
          hustleScore: parseInt(
            (totalHustlePoints / allGroupedSessions.length).toFixed(),
          ),
          // getMean drops null/undefined but keeps genuine zeros, matching how
          // the app aggregates these stats.
          hustleScoreTopLevel:
            getMean(allGroupedSessions.map(ps => ps.hustleScoreTopLevel)) ??
            undefined,
          energyRatio:
            getMean(allGroupedSessions.map(ps => ps.energyRatio)) ?? undefined,
          cumulative3DPlayerLoadV2: groupLoadData.length
            ? getSum(groupLoadData)
            : undefined,
          startTime: firstSession?.startTime,
          endTime: lastSession?.endTime,
          sessionDetail: lastSession?.sessionDetail,
        };
        return groupedPS.push({...ps, ...newSessionValues});
      });
    }
    return groupedPS;
  }, [playerSessions]);

  // A profile has one baseline row per session, so reduce to the most recent
  // baseline for each session type before comparing against it.
  const latestBaselines = useMemo(
    () => getLatestBaselineByType(baselines),
    [baselines],
  );

  const teamHasNumbers = useMemo(
    () => (playerSessions ?? []).some(ps => (ps.player?.number ?? 0) > 0),
    [playerSessions],
  );

  return (
    <TableContainer sx={{mt: 3, mb: 5}} component={Paper}>
      <Table aria-label={t('ariaLabels.tables.playerSessionsList') as string}>
        <TableBody>
          {[...(groupedPlayerSessions || [])]
            .sort((a: PlayerSession, b: PlayerSession) => {
              if (sortBy === 'teamPositionThenNameAsc') {
                return (
                  compare(
                    positions.indexOf(a.player?.profile?.position ?? ''),
                    positions.indexOf(b.player?.profile?.position ?? ''),
                  ) ||
                  compare(
                    a.player?.profile?.lastName,
                    b.player?.profile?.lastName,
                  )
                );
              } else {
                // Turn your strings into dates, and then subtract them
                // to get a value that is either negative, positive, or zero.
                return (
                  new Date(b.startTime as string).getTime() -
                  new Date(a.startTime as string).getTime()
                );
              }
            })
            .map((playerSession: PlayerSession) => (
              <PlayerSessionListItem
                key={playerSession.id}
                navigateUrl={
                  playerSession.teamSession?.teamSessionGroupId
                    ? `${groupNavigateUrlPrefix ?? navigateUrlPrefix}/groups/${playerSession.teamSession.teamSessionGroupId}`
                    : `${navigateUrlPrefix}/${playerSession.id}`
                }
                number={playerSession.player?.number}
                teamHasNumbers={teamHasNumbers}
                profileInfo={
                  showProfile
                    ? {
                        firstName: playerSession.player?.profile?.firstName,
                        lastName: playerSession.player?.profile?.lastName,
                        position: playerSession.player?.profile?.position,
                        profilePictureUrl:
                          playerSession.player?.profile?.profilePictureFileKey,
                      }
                    : undefined
                }
                type={playerSession.type as string}
                hasPlayerData={playerSession.hasPlayerData ?? false}
                startTime={
                  showDate
                    ? new Date(playerSession.startTime as string)
                    : undefined
                }
                endTime={
                  showDate
                    ? new Date(playerSession.endTime as string)
                    : undefined
                }
                activeTime={playerSession.activeTime}
                timeOnIce={playerSession.timeOnIce}
                energyRatio={playerSession.energyRatio}
                hustleScore={playerSession.hustleScore}
                hustleScoreTopLevel={playerSession.hustleScoreTopLevel}
                loadScore={playerSession.cumulative3DPlayerLoadV2}
                isGroupedSession={
                  !!playerSession.teamSession?.teamSessionGroupId
                }
                baseline={{
                  ...playerSession.baseline,
                  energyRatio:
                    latestBaselines[playerSession.type ?? '']?.energyRatio ??
                    playerSession.baseline?.energyRatio,
                }}
                localDisplayStartTime={playerSession.localDisplayStartTime}
                hasVideo={playerSession.hasVideo}
                teamName={
                  playerSession?.teamSession?.team?.displayName
                    ? playerSession?.teamSession?.team?.displayName
                    : t('general.individual')
                }
                sessionDetail={playerSession?.sessionDetail}
                timezone={playerSession?.timeZone}
              />
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PlayerSessionsList;
